import {
  USER_CARD_STAGE_INDEX,
  type UserCardStage,
} from "@-/dev.improve.haniverse.constants.user-card-stage";
import { isFsrsIntervalMastered } from "#is-user-card-mastered";

// A card's stage is decided by the FSRS review interval assigned at its last
// review — i.e. "how far out did this review push the next one". The interval
// (`scheduledDays`, the gap in days between the last review and the next due
// date) is the source of truth, NOT how much of that interval is left today, so
// a card's stage stays put between reviews instead of decaying toward "now".
//
// Bands (a week, four weeks, three months). These are chosen to line up with the interval
// ladder a card actually walks under our scheduler config (see `lib/cards/user-card/fsrs`): a
// card rated Easy every time runs 1d → 7d → 31d → 106d, so it advances exactly one stage per
// review, and each cutoff sits clear of the interval that crosses it (fuzz is enabled, so an
// interval landing exactly on a boundary would be fragile).
//   New         interval <  7 days
//   Familiar    interval >= 7 days   (a week or more)
//   Proficient  interval >= 28 days  (four weeks or more)
//   Mastered    interval >= 90 days  (three months or more — see `is-user-card-mastered`)
export const FAMILIAR_INTERVAL_DAYS = 7;
export const PROFICIENT_INTERVAL_DAYS = 28;

/**
 * Bucket an FSRS review interval (in days) into a {@link UserCardStage}. This is
 * the source of truth for the stage bands; see the constants above.
 */
export function getUserCardStageFromIntervalDays(scheduledDays: number): UserCardStage {
  if (isFsrsIntervalMastered(scheduledDays)) {
    return "mastered";
  }
  if (scheduledDays >= PROFICIENT_INTERVAL_DAYS) {
    return "proficient";
  }
  if (scheduledDays >= FAMILIAR_INTERVAL_DAYS) {
    return "familiar";
  }
  return "new";
}

// Narrow to the fields `getUserCardStage` actually reads, so callers feeding it
// audit-stripped FSRS rows (e.g. the panel user slice) don't have to widen back
// out to the full `FsrsCard` shape just to type-check.
type StagedFsrsCard = {
  lastReview: string | null;
  scheduledDays: number;
};

/**
 * Bucket a user card into one of the four user-facing stages. A never-reviewed
 * card (no FSRS row, or `lastReview` null) is "New"; a manually-mastered card is
 * "Mastered" regardless of its interval; otherwise the stage is the band of the
 * FSRS interval assigned at the card's last review (see
 * {@link getUserCardStageFromIntervalDays}).
 */
export function getUserCardStage(card: {
  fsrsCard: StagedFsrsCard | null;
  manuallyMasteredAt: string | null;
}): UserCardStage {
  if (card.manuallyMasteredAt != null) {
    return "mastered";
  }
  const fsrsCard = card.fsrsCard;
  if (fsrsCard === null || fsrsCard.lastReview === null) {
    // No real review history — every unreviewed, non-manually-mastered card is New.
    return "new";
  }
  return getUserCardStageFromIntervalDays(fsrsCard.scheduledDays);
}

/**
 * The weakest stage across a set of cards — useful when a category aggregates
 * multiple cards (e.g. a grapheme with several pronunciations) and the bar
 * should only advance once every card in the category has advanced.
 */
export function getMinUserCardStage(stages: readonly UserCardStage[]): UserCardStage {
  let min: UserCardStage | undefined;
  for (const stage of stages) {
    if (min === undefined || USER_CARD_STAGE_INDEX[stage] < USER_CARD_STAGE_INDEX[min]) {
      min = stage;
    }
  }
  return min ?? "new";
}
